import { Link } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Skeleton, SkeletonText } from '@/components/ui/Skeleton'
import { useClient } from '@/lib/clients-api'
import { cn } from '@/lib/utils'
import { DetailsCard } from './details'
import { DistributionCard } from './distribution'
import { MembersCard } from './members'
import { ProjectsCategoriesCard } from './projects'
import { RatesCard } from './rates'

export function ClientDetailPage({ clientId }: { clientId: string }) {
  const clientQuery = useClient(clientId)

  if (clientQuery.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-9 w-56" />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Card><SkeletonText lines={5} /></Card>
          <Card><SkeletonText lines={5} /></Card>
        </div>
      </div>
    )
  }

  if (clientQuery.isError || !clientQuery.data) {
    return (
      <div className="space-y-4">
        <Link to="/klanten" className="inline-flex items-center gap-1.5 text-sm text-ink-faint hover:text-ink">
          <ArrowLeft className="h-4 w-4" /> Klanten
        </Link>
        <p className="rounded-card border border-dashed border-hairline px-4 py-10 text-center text-sm text-ink-faint">Klant niet gevonden.</p>
      </div>
    )
  }

  const client = clientQuery.data

  return (
    <div className="space-y-6">
      <div>
        <Link to="/klanten" className="mb-3 inline-flex items-center gap-1.5 text-sm text-ink-faint hover:text-ink">
          <ArrowLeft className="h-4 w-4" /> Klanten
        </Link>
        <div className="flex items-center gap-3">
          <div
            aria-hidden="true"
            className={cn(
              'flex h-11 w-11 items-center justify-center rounded-xl text-base font-semibold',
              client.internal ? 'bg-fill-1 text-ink-faint' : 'text-white',
            )}
            style={client.internal ? undefined : { backgroundColor: client.brand.color }}
          >
            {client.companyName.slice(0, 2).toUpperCase()}
          </div>
          <div className="min-w-0">
            <h1 className="text-[28px] font-semibold leading-tight tracking-[-0.022em] text-ink">{client.companyName}</h1>
            {client.internal ? (
              <p className="mt-0.5 text-sm text-ink-faint">Interne klant · {client.brand.displayName} huisstijl</p>
            ) : (
              <p className="mt-0.5 flex items-center gap-1.5 text-sm text-ink-faint">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: client.brand.color }} />
                {client.brand.displayName}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <DetailsCard client={client} />
        <RatesCard client={client} />
        <DistributionCard client={client} />
        <ProjectsCategoriesCard client={client} />
        <MembersCard client={client} />
      </div>
    </div>
  )
}
